import { useState, useCallback } from 'react';

interface ToastData {
  id: string;
  type?: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
  duration?: number;
}

export const useToast = () => {
  const [toasts, setToasts] = useState<ToastData[]>([]);

  const addToast = useCallback((toast: Omit<ToastData, 'id'>) => {
    const id = Date.now().toString() + Math.random().toString(36).substr(2, 9);
    const newToast: ToastData = {
      id,
      ...toast,
    };

    setToasts(prev => [...prev, newToast]);
    return id;
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const showToast = useCallback((
    title: string,
    options?: {
      type?: 'success' | 'error' | 'warning' | 'info';
      message?: string;
      action?: {
        label: string;
        onClick: () => void;
      };
      duration?: number;
    }
  ) => {
    return addToast({
      title,
      type: options?.type || 'info',
      message: options?.message,
      action: options?.action,
      duration: options?.duration || 5000,
    });
  }, [addToast]);

  const showSuccess = useCallback((title: string, message?: string) => {
    return showToast(title, { type: 'success', message });
  }, [showToast]);

  const showError = useCallback((title: string, message?: string) => {
    return showToast(title, { type: 'error', message });
  }, [showToast]);

  const showWarning = useCallback((title: string, message?: string) => {
    return showToast(title, { type: 'warning', message });
  }, [showToast]);

  const showInfo = useCallback((title: string, message?: string) => {
    return showToast(title, { type: 'info', message });
  }, [showToast]);

  return {
    toasts,
    removeToast,
    showToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
  };
};